import type { Filter } from 'components/Blotters/Filter/Badges/hooks';
import { useSPIntl } from 'hooks/useIntl';
import type { IntlKey } from 'locales';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';

import { Badge } from './Badge';
export type FilterBadgeProps = {
  translationFunc: (colId: string) => IntlKey;
  column: Filter;
  onDismiss: (colId: string) => void;
  className?: string;
};

export const FilterBadge: FC<FilterBadgeProps> = ({
  column: { colId, values, isBooleanExpression },
  translationFunc,
  onDismiss,
  className,
}) => {
  const { formatMessage, concatFormatMessage } = useSPIntl();

  const formatValues = useCallback(
    (val: string | number | null) => {
      if (typeof val === 'number') {
        return val.toString();
      }
      if (typeof val === 'string' && val) {
        return concatFormatMessage('blotter.badges.filters.values', val);
      }
      if (val === undefined) {
        return '';
      }
      return val;
    },
    [concatFormatMessage],
  );

  const filterContent = useMemo(() => {
    const formattedValues = values.map(v => ({
      ...v,
      comparisonOperator: concatFormatMessage('blotter.badges.filters.comparisonOperator', v.comparisonOperator),
      boolOperator: v.boolOperator ? concatFormatMessage('blotter.badges.filters.booleanOperator', v.boolOperator) : '',
    }));
    if (formattedValues.length === 1) {
      return `${formattedValues[0].comparisonOperator} ${formatValues(formattedValues[0].value)}`;
    } else if (!isBooleanExpression) {
      return formatMessage({ id: 'blotter.badges.filters.selectedValues' }, { valuesCnt: formattedValues.length });
    } else if (values.length === 2 && isBooleanExpression) {
      return `${formattedValues[0].comparisonOperator} ${formattedValues[0].value} ${formattedValues[0].boolOperator} ${
        formattedValues[1].comparisonOperator
      } ${formatValues(formattedValues[1].value)}`;
    }
    return '';
  }, [values, formatMessage, isBooleanExpression, concatFormatMessage, formatValues]);

  return (
    <Badge id={colId} onDismiss={onDismiss} translationFunc={translationFunc} className={className}>
      {filterContent}
    </Badge>
  );
};
